import React, { useState, useEffect } from 'react';
import { useSubdomain } from '../contexts/SiteContext';
import sportsService from '../services/sportsService';
import './SiteForm.css';
import './MultiSportsSelector.css';


const SiteForm = ({ onSuccess, onCancel, editingSite = null }) => {
  const { createSite, updateSite } = useSubdomain();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sports: [], // NUEVO: Array de sport IDs
    sport_activity: '', // Mantener por compatibilidad
    logo: '',
    banner: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSportSelector, setShowSportSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sportActivities, setSportActivities] = useState({});
  const [sportsLoading, setSportsLoading] = useState(true);


  // Cargar deportes desde el backend
  useEffect(() => {
    const loadSports = async () => {
      try {
        setSportsLoading(true);
        const sports = await sportsService.getSportsByCategory();
        setSportActivities(sports);
      } catch (error) {
        console.error('Error loading sports:', error);
        // Fallback a datos estáticos si falla la carga
        setSportActivities({});
      } finally {
        setSportsLoading(false);
      }
    };

    loadSports();
  }, []);

  // Cargar datos si está editando
  useEffect(() => {
    if (editingSite) {
      setFormData({
        name: editingSite.name || '',
        description: editingSite.description || '',
        sports: editingSite.sports || [], // NUEVO: Array de sports
        sport_activity: editingSite.sport_activity || '', // Mantener por compatibilidad
        logo: editingSite.logo || '',
        banner: editingSite.banner || ''
      });
    }
  }, [editingSite]);

  // Cerrar selector de deportes al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSportSelector && !event.target.closest('.sport-selector-container')) {
        setShowSportSelector(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSportSelector]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };


  const getFilteredSports = () => {
    if (!searchTerm) return sportActivities;
    
    const filtered = {};
    Object.keys(sportActivities).forEach(category => {
      const activities = sportActivities[category].filter(activity =>
        activity.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (activities.length > 0) {
        filtered[category] = activities;
      }
    });
    return filtered;
  };


  // NUEVO: Manejar selección múltiple de sports
  const handleSportToggle = (sportId) => {
    setFormData(prev => ({
      ...prev,
      sports: prev.sports.includes(sportId)
        ? prev.sports.filter(id => id !== sportId)
        : [...prev.sports, sportId]
    }));
  };


  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
    if (!formData.description.trim()) newErrors.description = 'La descripción es requerida';
    
    // Validar sports
    if (!formData.sports || formData.sports.length === 0) {
      newErrors.sports = 'Debe seleccionar al menos un deporte';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      if (editingSite) {
        await updateSite(editingSite.id, formData);
      } else {
        await createSite(formData);
      }
      
      onSuccess?.();
    } catch (error) {
      console.error('Error guardando site:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="site-form">
      <div className="form-header">
        <h2>{editingSite ? 'Editar Centro Deportivo' : 'Crear Nuevo Centro Deportivo'}</h2>
        <p className="form-description">
          {editingSite 
            ? 'Actualiza la información de tu centro deportivo' 
            : 'Completa la información básica para crear tu centro deportivo'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="form-content">
        <div className="form-group">
          <label htmlFor="name">Nombre del Centro Deportivo *</label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={errors.name ? 'error' : ''}
            placeholder="Ej: Centro de Taekwondo Olimpo"
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        {/* NUEVO: Selector múltiple de deportes */}
        <div className="form-group">
          <label>Deportes Ofrecidos *</label>
          <div className="sports-selector-container">
            {sportsLoading ? (
              <div className="sports-loading">
                <span>Cargando deportes...</span>
              </div>
            ) : (
              <div className="sports-grid">
                {Object.entries(getFilteredSports()).map(([category, activities]) => (
                  <div key={category} className="sport-category">
                    <h4 className="category-title">{category}</h4>
                    <div className="sport-options">
                      {activities.map(activity => (
                        <div
                          key={activity.id}
                          className={`sport-card ${formData.sports.includes(activity.id) ? 'selected' : ''}`}
                          onClick={() => handleSportToggle(activity.id)}
                        >
                          <span className="sport-icon">{activity.icon}</span>
                          <span className="sport-name">{activity.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Indicador de deportes seleccionados */}
          {formData.sports && formData.sports.length > 0 && (
            <div className="selected-sports-indicator">
              <div className="selected-sports-count">
                <strong>Deportes seleccionados: {formData.sports.length}</strong>
              </div>
              <div style={{ fontSize: '12px', color: '#1976d2', marginTop: '4px' }}>
                IDs: {formData.sports.join(', ')}
              </div>
            </div>
          )}
          {errors.sports && <span className="error-message">{errors.sports}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="description">Descripción *</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className={errors.description ? 'error' : ''}
            rows="4"
            placeholder="Describe tu centro deportivo, su filosofía y especialidades..."
          />
          {errors.description && <span className="error-message">{errors.description}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="logo">Logo (URL)</label>
            <input
              type="url"
              id="logo"
              value={formData.logo}
              onChange={(e) => handleInputChange('logo', e.target.value)}
              placeholder="https://ejemplo.com/logo.png"
            />
          </div>

          <div className="form-group">
            <label htmlFor="banner">Banner (URL)</label>
            <input
              type="url"
              id="banner"
              value={formData.banner}
              onChange={(e) => handleInputChange('banner', e.target.value)}
              placeholder="https://ejemplo.com/banner.jpg"
            />
          </div>
        </div>


        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-outline"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Guardando...' : (editingSite ? 'Actualizar Centro Deportivo' : 'Crear Centro Deportivo')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SiteForm;
